import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Edit3, Trash2, Eye, EyeOff, GripVertical, LayoutList, CornerDownRight, Undo2, Archive, ArrowUpToLine, X, Save } from "lucide-react";

const DEFAULT_FORM = { label: "", path: "", visible: true, target: "_self", parent_id: "" };

export default function MenuManager() {
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [f, setF] = useState(DEFAULT_FORM);
  const [editing, setEditing] = useState(null); // full item object to edit
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/menu").then(r => setItems(r.data));
  const loadTrash = () => api.get("/admin/menu/trash").then(r => setTrash(r.data));
  useEffect(() => { load(); loadTrash(); }, []);

  // group into tree
  const { roots, childrenOf, byId } = useMemo(() => {
    const byId = Object.fromEntries(items.map(i => [i.id, i]));
    const roots = items.filter(i => !i.parent_id).sort((a, b) => a.order - b.order);
    const childrenOf = {};
    items.forEach(i => {
      if (i.parent_id) {
        (childrenOf[i.parent_id] ||= []).push(i);
      }
    });
    Object.values(childrenOf).forEach(list => list.sort((a, b) => a.order - b.order));
    return { roots, childrenOf, byId };
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const payload = { ...f, parent_id: f.parent_id || null, order: 999 };
      await api.post("/admin/menu", payload);
      toast.success("Menu item added");
      setF(DEFAULT_FORM); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const edit = (it) => setEditing(it);
  const del = async (id) => {
    if (!confirm("Move this menu item to the Recycle Bin? Any children will also be moved. You can restore them later.")) return;
    // recurse: soft-delete children first so they can each be restored independently
    const kids = childrenOf[id] || [];
    for (const k of kids) await api.delete(`/admin/menu/${k.id}`);
    await api.delete(`/admin/menu/${id}`);
    toast.success("Moved to Recycle Bin");
    load(); loadTrash();
  };
  const toggleVisible = async (it) => {
    setBusy(`vis-${it.id}`);
    try { await api.put(`/admin/menu/${it.id}`, { ...it, visible: !it.visible, parent_id: it.parent_id || null }); load(); }
    catch { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  const promote = async (it) => {
    if (!it.parent_id) return;
    setBusy(`pro-${it.id}`);
    try {
      // move to top-level and append to end of the root list
      const nextOrder = roots.length;
      await api.put(`/admin/menu/${it.id}`, { ...it, parent_id: null, order: nextOrder });
      toast.success("Moved to main menu");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
    finally { setBusy(null); }
  };

  const demote = async (it, newParentId) => {
    if (!newParentId || newParentId === it.id) return;
    if ((childrenOf[it.id] || []).length > 0) {
      toast.error("This item has its own sub-menu. Move or promote those sub-items first.");
      return;
    }
    setBusy(`dem-${it.id}`);
    try {
      const nextOrder = (childrenOf[newParentId] || []).length;
      await api.put(`/admin/menu/${it.id}`, { ...it, parent_id: newParentId, order: nextOrder });
      toast.success("Moved into sub-menu");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
    finally { setBusy(null); }
  };

  const restore = async (id) => {
    setBusy(`res-${id}`);
    try {
      await api.post(`/admin/menu/${id}/restore`);
      toast.success("Restored");
      load(); loadTrash();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusy(null); }
  };
  const purge = async (id) => {
    if (!confirm("Permanently delete this menu item? This cannot be undone.")) return;
    setBusy(`pur-${id}`);
    try {
      await api.delete(`/admin/menu/${id}/purge`);
      toast.success("Permanently deleted");
      loadTrash();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  const persistOrder = async (newList, parent_id) => {
    const payload = newList.map((it, i) => ({ id: it.id, order: i, parent_id: parent_id || null }));
    setItems(prev => {
      const other = prev.filter(p => !newList.find(n => n.id === p.id));
      return [...other, ...newList.map((n, i) => ({ ...n, order: i, parent_id: parent_id || null }))];
    });
    try { await api.post("/admin/menu/reorder", { items: payload }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="menu-manager">
      {/* Form */}
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-2 text-sky-700"><LayoutList className="w-4 h-4"/><span className="overline">Add menu item</span></div>
        <p className="text-sm text-slate-600 mb-4">Menu items appear in the site header nav. Drag rows to reorder. Set a <strong>Parent</strong> to make an item a sub-menu (shown as a dropdown on hover). Click the <strong>pencil</strong> on any row to rename it. Deleted items go to the <strong>Recycle Bin</strong> below and can be restored.</p>
        <form onSubmit={submit} className="grid md:grid-cols-6 gap-2">
          <input required placeholder="Label (e.g. EdTech)" value={f.label} onChange={e => setF({...f, label: e.target.value})} className="md:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="menu-label-input"/>
          <input required placeholder="/category/... or /rankings or https://..." value={f.path} onChange={e => setF({...f, path: e.target.value})} className="md:col-span-3 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="menu-path-input"/>
          <select value={f.target} onChange={e => setF({...f, target: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm">
            <option value="_self">Same tab</option>
            <option value="_blank">New tab</option>
          </select>
          <select value={f.parent_id} onChange={e => setF({...f, parent_id: e.target.value})} className="md:col-span-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm" data-testid="menu-parent-select">
            <option value="">— Top-level (no parent) —</option>
            {roots.map(r => (
              <option key={r.id} value={r.id}>Under: {r.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-3">
            <input type="checkbox" checked={f.visible} onChange={e => setF({...f, visible: e.target.checked})}/>
            Visible in nav
          </label>
          <div className="md:col-span-6 flex gap-2">
            <button disabled={busy==="save"} className="px-5 py-2.5 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="menu-save-btn"><Plus className="w-4 h-4"/>Add item</button>
          </div>
        </form>
      </div>

      {/* Top-level list with drag */}
      <SortableGroup
        list={roots}
        parent_id={null}
        onReorder={(newList) => persistOrder(newList, null)}
        renderExtra={(it) => (
          (childrenOf[it.id] || []).length > 0 && (
            <div className="ml-10 mb-4 pl-4 border-l-2 border-sky-100" data-testid={`sub-list-${it.id}`}>
              <div className="overline text-[10px] text-sky-700 mb-2 flex items-center gap-1"><CornerDownRight className="w-3 h-3"/>Sub-menu of {it.label}</div>
              <SortableGroup
                list={childrenOf[it.id]}
                parent_id={it.id}
                onReorder={(newList) => persistOrder(newList, it.id)}
                onEdit={edit} onDelete={del} onToggleVisible={toggleVisible} onPromote={promote} busy={busy}
              />
            </div>
          )
        )}
        onEdit={edit} onDelete={del} onToggleVisible={toggleVisible} onDemote={demote} roots={roots} childrenOf={childrenOf} busy={busy}
      />

      <p className="text-xs text-slate-500">💡 Drag the ⋮⋮ handle to reorder. Assign a Parent from the form dropdown to nest items. Deletes are recoverable from the Recycle Bin.</p>

      {/* Recycle Bin */}
      <div className="card-soft overflow-hidden" data-testid="menu-recycle-bin">
        <button
          type="button"
          onClick={() => setShowTrash(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50"
          data-testid="menu-recycle-toggle"
        >
          <div className="flex items-center gap-2 text-slate-700">
            <Archive className="w-4 h-4 text-slate-500"/>
            <span className="overline">Recycle Bin</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="menu-recycle-count">{trash.length}</span>
          </div>
          <span className="text-xs text-slate-500">{showTrash ? "Hide" : "Show"}</span>
        </button>
        {showTrash && (
          <div className="border-t border-slate-100">
            {trash.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="menu-recycle-empty">Recycle bin is empty. Deleted menu items will appear here.</div>
            ) : (
              <ul className="divide-y divide-slate-50">
                {trash.map(it => (
                  <li key={it.id} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50" data-testid={`menu-recycle-row-${it.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center text-slate-400"><Trash2 className="w-4 h-4"/></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-editorial font-semibold text-slate-800 truncate">{it.label}</div>
                      <div className="text-xs text-slate-500 truncate">{it.path} · deleted {it.deleted_at ? new Date(it.deleted_at).toLocaleString() : "—"}</div>
                    </div>
                    <button
                      onClick={() => restore(it.id)}
                      disabled={busy===`res-${it.id}`}
                      className="px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center gap-1 disabled:opacity-60"
                      data-testid={`menu-restore-${it.id}`}
                    >
                      <Undo2 className="w-3.5 h-3.5"/>Restore
                    </button>
                    <button
                      onClick={() => purge(it.id)}
                      disabled={busy===`pur-${it.id}`}
                      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-60"
                      title="Delete permanently"
                      data-testid={`menu-purge-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {editing && (
        <EditMenuItemModal
          item={editing}
          roots={roots}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditMenuItemModal({ item, roots, onClose, onSaved }) {
  const [f, setF] = useState({
    label: item.label || "",
    path: item.path || "",
    visible: !!item.visible,
    target: item.target || "_self",
    parent_id: item.parent_id || "",
    order: item.order || 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.label.trim() || !f.path.trim()) { toast.error("Label and path are required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/menu/${item.id}`, { ...f, parent_id: f.parent_id || null });
      toast.success("Menu item updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const parentOptions = (roots || []).filter(r => r.id !== item.id);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="edit-menu-modal">
      <div className="bg-white rounded-3xl max-w-xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="edit-menu-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">Edit menu item</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6 truncate pr-10" title={item.label}>{item.label}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Label (name shown in nav)</label>
            <input autoFocus required value={f.label} onChange={e => setF({...f, label: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="edit-menu-label"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Path / URL</label>
            <input required value={f.path} onChange={e => setF({...f, path: e.target.value})} placeholder="/category/... or /rankings or https://..." className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="edit-menu-path"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Open in</label>
              <select value={f.target} onChange={e => setF({...f, target: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm">
                <option value="_self">Same tab</option>
                <option value="_blank">New tab</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Parent</label>
              <select value={f.parent_id} onChange={e => setF({...f, parent_id: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="edit-menu-parent">
                <option value="">— Top-level (no parent) —</option>
                {parentOptions.map(r => (
                  <option key={r.id} value={r.id}>Under: {r.label}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.visible} onChange={e => setF({...f, visible: e.target.checked})} data-testid="edit-menu-visible"/>
            Visible in header nav
          </label>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="edit-menu-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : "Save changes"}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableGroup({ list, parent_id, onReorder, renderExtra, onEdit, onDelete, onToggleVisible, onPromote, onDemote, roots, childrenOf, busy }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [local, setLocal] = useState(list);
  useEffect(() => { setLocal(list); }, [list]);

  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = local.findIndex(i => i.id === active.id);
    const newIndex = local.findIndex(i => i.id === over.id);
    const newList = arrayMove(local, oldIndex, newIndex);
    setLocal(newList);
    onReorder(newList);
  };

  return (
    <div className="card-soft overflow-hidden" data-testid={`menu-group-${parent_id || "root"}`}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={local.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <ul className="divide-y divide-slate-50">
            {local.map(it => (
              <div key={it.id}>
                <SortableRow it={it} onEdit={onEdit} onDelete={onDelete} onToggleVisible={onToggleVisible} onPromote={onPromote} onDemote={onDemote} roots={roots} childrenOf={childrenOf} busy={busy}/>
                {renderExtra && renderExtra(it)}
              </div>
            ))}
            {local.length === 0 && <li className="p-6 text-center text-sm text-slate-500">Empty.</li>}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableRow({ it, onEdit, onDelete, onToggleVisible, onPromote, onDemote, roots, childrenOf, busy }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isRoot = !it.parent_id;
  const hasChildren = isRoot && (childrenOf?.[it.id] || []).length > 0;
  const demoteTargets = isRoot && !hasChildren ? (roots || []).filter(r => r.id !== it.id) : [];
  return (
    <li ref={setNodeRef} style={style} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-sky-50/30" data-testid={`menu-row-${it.id}`}>
      <button {...attributes} {...listeners} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-700 cursor-grab active:cursor-grabbing" aria-label="Drag" data-testid={`menu-drag-${it.id}`}>
        <GripVertical className="w-4 h-4"/>
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-editorial font-semibold text-slate-900">{it.label}</div>
        <div className="text-xs text-slate-500 truncate">{it.path}</div>
      </div>
      {it.visible
        ? <span className="overline text-[10px] px-2 py-1 rounded-full tag-green">visible</span>
        : <span className="overline text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-500">hidden</span>}
      {it.parent_id && onPromote && (
        <button
          onClick={() => onPromote(it)}
          disabled={busy===`pro-${it.id}`}
          className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 disabled:opacity-60"
          title="Move to main menu (promote to top-level)"
          data-testid={`menu-promote-${it.id}`}
        >
          <ArrowUpToLine className="w-4 h-4"/>
        </button>
      )}
      {isRoot && onDemote && demoteTargets.length > 0 && (
        <select
          value=""
          onChange={(e) => { if (e.target.value) onDemote(it, e.target.value); }}
          disabled={busy===`dem-${it.id}`}
          className="text-xs px-2 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-sky-300 max-w-[130px]"
          title="Move into sub-menu of…"
          data-testid={`menu-demote-${it.id}`}
        >
          <option value="">Move under…</option>
          {demoteTargets.map(r => (
            <option key={r.id} value={r.id}>Under: {r.label}</option>
          ))}
        </select>
      )}
      <button onClick={() => onToggleVisible(it)} disabled={busy===`vis-${it.id}`} className="p-2 rounded-lg bg-slate-100 hover:bg-emerald-100 text-slate-600" data-testid={`menu-toggle-${it.id}`}>{it.visible ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}</button>
      <button onClick={() => onEdit(it)} className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600" data-testid={`menu-edit-${it.id}`}><Edit3 className="w-4 h-4"/></button>
      <button onClick={() => onDelete(it.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`menu-del-${it.id}`} title="Move to Recycle Bin"><Trash2 className="w-4 h-4"/></button>
    </li>
  );
}
